import { Controller } from "@hotwired/stimulus"

const URL_PARAM = 'open_carrier'

export default class extends Controller {
  static targets = ["panel", "overlay", "frame", "loading", "content"]

  // Arrow function properties to avoid rebinding issues during Turbo navigation
  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isPanelOpen()) {
      this.closePanel()
    }
  }

  handleFrameError = (event) => {
    // Prevent global turbo:frame-missing handler from navigating away
    event?.stopPropagation()
    event?.preventDefault()

    // Clear timeout since we're handling the error
    this.clearLoadTimeout()

    console.error('[carrier-panel] Frame load error:', {
      url: this.frameTarget?.src,
      timestamp: new Date().toISOString()
    })

    if (this.hasLoadingTarget) {
      this.loadingTarget.innerHTML = `
        <div class="text-center text-danger">
          <i class="ph ph-warning" style="font-size: 2rem;"></i>
          <p class="mt-2 mb-0">Failed to load carrier information.</p>
          <p class="small">Please try again later.</p>
        </div>
      `
    }
  }

  clearLoadTimeout() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
      this.loadTimeout = null
    }
  }

  connect() {
    document.addEventListener('keydown', this.handleKeydown)
    this._checkAutoOpenFromUrl()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  isPanelOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains('open')
  }

  // Fired by each card's title toggle (a real <button>, so Enter / Space
  // arrive as native clicks — no keydown handler and no propagation guard on
  // the sibling row actions is needed, #13363).
  openPanel(event) {
    event?.preventDefault()
    event?.stopPropagation()

    const clickedElement = event?.currentTarget || event?.target
    const carrierId = clickedElement?.dataset?.carrierId
    this._openForCarrierId(carrierId)
  }

  closePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove('open')
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('open')
    }
    document.body.style.overflow = ''

    // Clean up listeners and timeout since panel may close before frame loads
    // (once: true doesn't help if the event never fires)
    this.clearLoadTimeout()
    if (this.hasFrameTarget) {
      this.frameTarget.removeEventListener('turbo:frame-missing', this.handleFrameError)
      this.frameTarget.removeEventListener('turbo:fetch-request-error', this.handleFrameError)
    }
  }

  frameLoaded() {
    // Clear timeout since frame loaded successfully
    this.clearLoadTimeout()

    // Hide loading state, show content
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove('d-none')
    }
  }

  _openForCarrierId(carrierId) {
    if (!carrierId || !/^\d+$/.test(carrierId)) {
      console.error('[carrier-panel] Invalid carrier ID')
      return
    }

    if (!this.hasPanelTarget || !this.hasOverlayTarget) {
      console.error('[carrier-panel] Panel or overlay target not found')
      return
    }

    this.panelTarget.classList.add('open')
    this.overlayTarget.classList.add('open')
    document.body.style.overflow = 'hidden'

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.add('d-none')
    }

    if (this.hasFrameTarget) {
      this.frameTarget.addEventListener('turbo:frame-missing', this.handleFrameError, { once: true })
      this.frameTarget.addEventListener('turbo:fetch-request-error', this.handleFrameError, { once: true })
      this.frameTarget.src = `/carriers/${carrierId}/quick_access`

      // Timeout fallback for cases where no error event fires (e.g., hanging requests)
      this.loadTimeout = setTimeout(() => {
        if (this.hasLoadingTarget && !this.loadingTarget.classList.contains('d-none')) {
          this.handleFrameError()
        }
      }, 15000)
    }

    requestAnimationFrame(() => {
      const closeBtn = this.panelTarget.querySelector('.gl-panel-close')
      closeBtn?.focus()
    })
  }

  // Mirrors the ?open_order=<id> deep-link pattern.
  // Strips the param after reading so the URL doesn't persist across reloads.
  _checkAutoOpenFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const carrierId = params.get(URL_PARAM)
    if (!carrierId) return

    const url = new URL(window.location)
    url.searchParams.delete(URL_PARAM)
    window.history.replaceState({}, '', url)

    this._openForCarrierId(carrierId)
  }
}
